import { useEffect, useRef, useState } from 'react';
import type { TaskSummary } from '../../data/client';
import {
  useAnnotationUsers,
  useCreateTask,
  useDeleteTask,
  useTasks,
  useUpdateTask,
} from '../../data/hooks';
import {
  Button,
  Checkbox,
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  GroupedForm,
  Icon,
  IconButton,
  Input,
  Skeleton,
  type TagTone,
  useToast,
} from '../../ui';
import { dueBadge } from './dueDate';
import { TaskEditor } from './TaskEditor';

/**
 * OPEN TASKS ON THE RECORD, under the field list (notes-tasks-comments.md 12.1).
 *
 * TASKS ARE STATE, so they live with the details rather than in the Activity feed. A
 * task is what is true about this record NOW; burying it in a history-ordered feed is
 * how tasks die. Notes and comments went the other way for the mirror-image reason -
 * they are history, and history belongs in the feed.
 *
 * Done tasks collapse behind a count: they are evidence, not work, and a section that
 * grows forever stops being scanned.
 */
export interface TaskSectionProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly locale?: string | undefined;
}

export function TaskSection({ objectId, recordId, locale }: TaskSectionProps) {
  const tasks = useTasks(objectId, recordId);
  const createTask = useCreateTask(objectId, recordId);
  const [title, setTitle] = useState('');
  /** Whether the inline new row is standing at the end of the list (wave 4, C2-13). */
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [editing, setEditing] = useState<TaskSummary | null>(null);

  const all = tasks.data ?? [];
  const open = all.filter((task) => task.doneAt === null);
  const done = all.filter((task) => task.doneAt !== null);

  /**
   * ENTER COMMITS AND LEAVES THE ROW STANDING, which is what Reminders does: "You can also
   * click an existing reminder, then press Return to quickly add more reminders to the same
   * list".
   * support: https://support.apple.com/guide/reminders/add-or-change-reminders-remndc729e28/mac
   */
  const commit = (): void => {
    const trimmed = title.trim();
    if (trimmed === '') {
      setAdding(false);
      return;
    }
    setTitle('');
    createTask.mutate({ title: trimmed });
  };
  /** Escape, and a blur with nothing typed, take the row away again. */
  const discard = (): void => {
    setTitle('');
    setAdding(false);
  };

  /**
   * AN EMPTY SECTION KEEPS ITS HEADING AND SHRINKS TO ONE MUTED LINE (BR-RECORD; handbook
   * DESIGN.md H4: "Shrink an empty section in vertical page flow to one muted line that says
   * what belongs there and what happens next, with its inline action", D12, N21; register
   * A6-13). The heading names the section; under it the sentence and its action are one row,
   * where they were two (the sentence, then the footer row). The words are the ones the section
   * already had. Never on a failed load: an error is not "nothing to do" (H7).
   */
  const empty = !tasks.isPending && !tasks.isError && all.length === 0;

  return (
    /*
      WAVE 3 (R-B1): THE GROUP BOX, LIKE EVERY OTHER SECTION OF THE OVERVIEW. Tasks were the
      one section still drawn as a pane title over loose rows with an `EmptyState` block and a
      free-floating input pair under them; they are now the kit's grouped list - rows with
      hairlines, the composer as the list's own control row, and the done count as a footer
      row - so a reader meets one construction from the top of the Overview to its end.
      kit: Apple macOS 27 UI Kit.sketch / Group Boxes/Light, Forms/Example Forms/Light
    */
    <GroupedForm title="Open tasks">
      {tasks.isPending ? (
        <div className="record-row record-row-stacked" aria-busy="true">
          <Skeleton className="h-3 w-40" label="Loading the tasks" />
          <Skeleton className="h-3 w-32" />
        </div>
      ) : (
        <>
          {open.length === 0 && done.length === 0 ? (
            adding ? null : (
              <p className="record-row record-row-stacked text-sm text-ink-muted">
                <span>
                  No tasks. A task is one line somebody has to do, with a day and a name on it.{' '}
                  {empty && (
                    <Button variant="text" onClick={() => setAdding(true)}>
                      Add task…
                    </Button>
                  )}
                </span>
              </p>
            )
          ) : (
            <ul>
              {open.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  locale={locale}
                  objectId={objectId}
                  recordId={recordId}
                  onEdit={() => setEditing(task)}
                />
              ))}
            </ul>
          )}

          {/*
            WAVE 4 (C2-13): THE NEW TASK IS AN INLINE ROW, NOT A FIELD WITH A BUTTON BESIDE IT.
            What stood here was a bordered text field and an "Add" push button inside a taller
            control row - a web form at the end of a list of kit rows, which is what owner-87
            shows. Reminders adds a reminder as a new row in the list with its own empty
            circle, and the way in is the list's own footer row, which is the construction the
            related lists on this same screen already use.
            support: https://support.apple.com/guide/reminders/add-or-change-reminders-remndc729e28/mac
            kit: Apple macOS 27 UI Kit.sketch / Lists/Light/Footer/Footer
            OPEN: Apple's page describes clicking Add and typing a title and does not use the
            words "empty row" or "empty circle"; the circle is the task's OWN unchecked control,
            which is the one this list already draws on every row. Searched
            support.apple.com/guide/reminders for "Add a reminder on Mac", "empty row" and
            "empty circle".
          */}
          {adding ? (
            <NewTaskRow
              value={title}
              onChange={setTitle}
              onCommit={commit}
              onDiscard={discard}
              pending={createTask.isPending}
            />
          ) : empty ? null : (
            <button
              type="button"
              className="record-row record-group-action"
              onClick={() => setAdding(true)}
            >
              <Icon name="plus" size="0.9rem" />
              <span>Add task…</span>
            </button>
          )}

          {done.length > 0 && (
            <>
              {/* THE FOOTER ROW OF THE LIST, in sentence case with the verb the HIG's Writing
                  page asks a button label for. A count in letter-spaced capitals has no master
                  in either kit; the row is the same footer construction the related lists use.
                  hig: https://developer.apple.com/design/human-interface-guidelines/writing */}
              <button
                type="button"
                aria-expanded={showDone}
                onClick={() => setShowDone((value) => !value)}
                className="record-row record-group-action"
              >
                <Icon name="chevron-right" size="0.9rem" className={showDone ? 'rotate-90' : ''} />
                <span>{`${showDone ? 'Hide' : 'Show'} ${String(done.length)} done`}</span>
              </button>
              {showDone && (
                <ul>
                  {done.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      locale={locale}
                      objectId={objectId}
                      recordId={recordId}
                      onEdit={() => setEditing(task)}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}

      {editing !== null && (
        <TaskEditor
          task={editing}
          objectId={objectId}
          recordId={recordId}
          open
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
        />
      )}
    </GroupedForm>
  );
}

/**
 * THE ROW A NEW TASK IS TYPED INTO: the task's own unchecked circle, and the title as an
 * unbordered field in the row's own type. It is unbordered because the ROW is the control
 * here - a bordered box inside a grouped list row is the second frame the HIG's layout rule
 * forbids, and the circle beside it already says this is a task.
 *
 * The circle is DISABLED rather than absent: a task nobody has named cannot be finished, and
 * a control that appears the instant the title lands would move the row under the pointer.
 */
function NewTaskRow({
  value,
  onChange,
  onCommit,
  onDiscard,
  pending,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onCommit: () => void;
  readonly onDiscard: () => void;
  readonly pending: boolean;
}) {
  const field = useRef<HTMLInputElement>(null);
  /** The row appears because somebody asked for it, so the caret is already in it. */
  useEffect(() => {
    field.current?.focus();
  }, []);

  return (
    <div className="record-row record-row-stacked" aria-busy={pending || undefined}>
      <div className="flex items-center gap-3">
        <Checkbox checked={false} onCheckedChange={() => undefined} ariaLabel="New task" disabled />
        {/*
          THE FIELD IS THE PRIMITIVE, IN ITS BARE SHELL (R1-10, review failure 10; E1 round
          1). A raw HTML field carries no field context, so the kit's field treatment can
          never reach it; `bare` is the shell the unbordered row wants and
          `.record-task-field` keeps stating the row's own type size, height floor and
          padding. Nothing else in this file changed.
        */}
        <Input
          bare
          ref={field}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onCommit();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              onDiscard();
            }
          }}
          onBlur={() => {
            if (value.trim() === '') onDiscard();
          }}
          placeholder="New task"
          aria-label="New task"
          className="record-task-field"
        />
      </div>
    </div>
  );
}

interface TaskRowProps {
  readonly task: TaskSummary;
  readonly objectId: string;
  readonly recordId: string;
  readonly locale?: string | undefined;
  readonly onEdit: () => void;
}

function TaskRow({ task, objectId, recordId, onEdit, locale }: TaskRowProps) {
  const updateTask = useUpdateTask(objectId, recordId);
  const deleteTask = useDeleteTask(objectId, recordId);
  const users = useAnnotationUsers();
  const toast = useToast();
  const done = task.doneAt !== null;
  const badge = dueBadge(task.dueDate, done, locale);

  const assignee = users.data?.find((user) => user.id === task.assigneeUserId);

  const remove = () =>
    deleteTask.mutate(task.id, {
      /** Deletion is the one routine action worth a toast: it is the reversible-looking one that is not. */
      onSuccess: () => toast({ title: 'Task deleted', tone: 'neutral' }),
    });

  const row = (
    /*
      WAVE 3 (R-B1): THE KIT'S LIST ROW - a leading checkbox, the title, one secondary line,
      one trailing accessory - and its due date and its writer said in words.
      `Badge` painted a capsule around each of them, which the owner's round-2 decision took
      off every read surface of this screen; a due date is not a tag, it is a fact with a
      colour, so it keeps the tone and loses the fill.

      WAVE 4 ROUND 2: AND IT DRAWS NOTHING AT REST. The ellipsis stood open on every task
      while the comment and junction rows beside it had already been given the kit's rule in
      round 1 - one screen, two answers to the same question. Reminders shows a reminder's
      own control only on the row somebody is pointing at; here the row's actions are its
      context menu, which the secondary click and the touch-and-hold both reach, and the More
      appears under the pointer through `.record-row-hover`, which is the pair the junction
      row already states.
      support: https://support.apple.com/guide/reminders/add-or-change-reminders-remndc729e28/mac
      kit: Apple macOS 27 UI Kit.sketch / Lists/Light/Rows/Default
      hig: https://developer.apple.com/design/human-interface-guidelines/context-menus
    */
    <div className="record-row-hover items-start">
      <Checkbox
        checked={done}
        onCheckedChange={(checked) =>
          updateTask.mutate({ taskId: task.id, patch: { done: checked } })
        }
        ariaLabel={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
      />
      <div className="min-w-0 flex-1">
        <p className={done ? 'text-sm text-ink-subtle line-through' : 'text-sm text-ink'}>
          {task.title}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs">
          {badge !== null && <span className={DUE_INK[badge.tone]}>{badge.label}</span>}
          {assignee !== undefined && (
            <span className="text-ink-subtle">
              {assignee.name}
              {/* Edge case 1: nothing auto-reassigns, so stale work has to be visible. */}
              {!assignee.isActive && ' (deactivated)'}
            </span>
          )}
          {task.createdBy.source !== 'MANUAL' && (
            <span className="font-medium text-accent">
              {task.createdBy.source === 'AGENT' ? 'AI assistant' : task.createdBy.source}
            </span>
          )}
        </div>
      </div>

      <span className="record-row-more">
        <DropdownMenu
          trigger={
            <IconButton icon="more-horizontal" label={`Actions for ${task.title}`} size="sm" />
          }
          align="end"
        >
          <DropdownItem icon="pencil" onSelect={onEdit}>
            Edit
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem icon="trash" tone="danger" onSelect={remove}>
            Delete
          </DropdownItem>
        </DropdownMenu>
      </span>
    </div>
  );

  return (
    <li className="record-row record-row-stacked">
      <ContextMenu trigger={row}>
        <ContextMenuItem icon="pencil" onSelect={onEdit}>
          Edit
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem icon="trash" tone="danger" onSelect={remove}>
          Delete
        </ContextMenuItem>
      </ContextMenu>
    </li>
  );
}

/**
 * The due state's own colour role, without the capsule `Badge` drew around it. The tones are
 * `dueDate.ts`'s and unchanged; what changed is that they now colour the WORDS.
 */
const DUE_INK: Record<TagTone, string> = {
  neutral: 'text-ink-muted',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
};
